import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../api/client';
import { useAuth, hasRole } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import {
  MapPin, Camera, ExternalLink, Locate, Save, Trash2, ImagePlus, CheckCircle2, XCircle, Clock, History,
} from 'lucide-react';
import { formatDateTime } from '../lib/format';

/*
 * EquipmentLocationPhotos.jsx
 * Dialog with two side-by-side panels:
 *  - Location: lat/lng inputs + "Use my GPS" + map link + history
 *  - Photos: client-side compressed upload + grid w/ approve/reject/delete
 */

const TABS = [
  { k: 'location', label: 'Location', icon: MapPin },
  { k: 'photos', label: 'Photos', icon: Camera },
];

// Compress an image File/Blob to a base64 JPEG ≤ ~200KB
const compressImage = (file, { maxWidth = 1200, quality = 0.7, maxKB = 200 } = {}) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;
        if (width > maxWidth) { height *= maxWidth / width; width = maxWidth; }
        const canvas = document.createElement('canvas');
        canvas.width = width; canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        let q = quality;
        let dataUrl = canvas.toDataURL('image/jpeg', q);
        // iterate down quality until small enough
        while ((dataUrl.length * 0.75) / 1024 > maxKB && q > 0.2) {
          q -= 0.1;
          dataUrl = canvas.toDataURL('image/jpeg', q);
        }
        resolve(dataUrl);
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const EquipmentLocationPhotos = ({ open, onClose, vehicle, onChanged }) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tab, setTab] = useState('location');
  const [locInfo, setLocInfo] = useState({ location: null, pending_location: null, history: [] });
  const [photos, setPhotos] = useState([]);
  const [lat, setLat] = useState('');
  const [lng, setLng] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [photoBusy, setPhotoBusy] = useState(false);
  const fileRef = useRef(null);
  const [pendingPhotos, setPendingPhotos] = useState([]); // [{file, dataUrl, caption}]

  const canEditLocation = hasRole(user, 'super_admin', 'admin', 'supervisor', 'approver', 'operator');
  const canUploadPhoto = hasRole(user, 'super_admin', 'admin', 'supervisor', 'operator');
  const canApprove = hasRole(user, 'super_admin', 'admin', 'approver');
  const canDeletePhotoAny = hasRole(user, 'super_admin', 'admin');

  const load = useCallback(async () => {
    if (!vehicle?.id) return;
    try {
      const [loc, ph] = await Promise.all([
        api.get(`/vehicles/${vehicle.id}/location-history`),
        api.get(`/vehicles/${vehicle.id}/photos`),
      ]);
      setLocInfo(loc.data);
      setPhotos(ph.data || []);
      if (loc.data?.location) {
        setLat(String(loc.data.location.lat ?? ''));
        setLng(String(loc.data.location.lng ?? ''));
        setLabel(loc.data.location.label || '');
      }
    } catch (e) { /* ignore */ }
  }, [vehicle?.id]);

  // The parent unmounts this component on close (sets vehicle=null), so each
  // open is a fresh mount — no manual reset needed. Just load data on mount.
  useEffect(() => { load(); }, [load]);

  const useGps = () => {
    if (!navigator.geolocation) { toast({ title: 'Geolocation not supported on this device', variant: 'destructive' }); return; }
    toast({ title: 'Getting GPS fix…' });
    navigator.geolocation.getCurrentPosition(
      (pos) => { setLat(pos.coords.latitude.toFixed(6)); setLng(pos.coords.longitude.toFixed(6)); toast({ title: 'GPS captured', description: `Accuracy: ±${Math.round(pos.coords.accuracy)} m` }); },
      (err) => toast({ title: 'GPS failed', description: err.message, variant: 'destructive' }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const saveLocation = async () => {
    const la = Number(lat); const ln = Number(lng);
    if (!Number.isFinite(la) || !Number.isFinite(ln)) { toast({ title: 'Lat/lng must be numbers', variant: 'destructive' }); return; }
    setBusy(true);
    try {
      const r = await api.put(`/vehicles/${vehicle.id}/location`, { lat: la, lng: ln, label: label || null, source: 'manual' });
      toast({ title: r.data.status === 'pending' ? 'Submitted for approval' : 'Location updated' });
      onChanged?.(); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail || e.message, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const decideLocation = async (decision) => {
    setBusy(true);
    try {
      await api.post(`/vehicles/${vehicle.id}/location/${decision}`);
      toast({ title: `Location ${decision}d` });
      onChanged?.(); load();
    } catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setBusy(false); }
  };

  const onFiles = async (files) => {
    setPhotoBusy(true);
    try {
      const arr = Array.from(files || []);
      const out = [];
      for (const f of arr) {
        if (!f.type.startsWith('image/')) continue;
        const dataUrl = await compressImage(f);
        // Stable client-side id so React keys survive add/remove/caption edits.
        const _key = (typeof crypto !== 'undefined' && crypto.randomUUID)
          ? crypto.randomUUID()
          : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
        out.push({ _key, name: f.name, dataUrl, caption: '' });
      }
      setPendingPhotos((prev) => [...prev, ...out]);
    } finally { setPhotoBusy(false); }
  };

  const uploadAll = async () => {
    setPhotoBusy(true);
    try {
      for (const p of pendingPhotos) {
        await api.post(`/vehicles/${vehicle.id}/photos`, { image_base64: p.dataUrl, caption: p.caption || null, taken_at: new Date().toISOString() });
      }
      toast({ title: `${pendingPhotos.length} photo(s) uploaded` });
      setPendingPhotos([]); onChanged?.(); load();
    } catch (e) { toast({ title: 'Upload failed', description: e.response?.data?.detail, variant: 'destructive' }); }
    finally { setPhotoBusy(false); }
  };

  const decidePhoto = async (id, decision) => {
    try { await api.post(`/vehicles/${vehicle.id}/photos/${id}/${decision}`); toast({ title: `Photo ${decision}d` }); onChanged?.(); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  const deletePhoto = async (id) => {
    if (!window.confirm('Delete this photo?')) return;
    try { await api.delete(`/vehicles/${vehicle.id}/photos/${id}`); toast({ title: 'Photo deleted' }); onChanged?.(); load(); }
    catch (e) { toast({ title: 'Failed', description: e.response?.data?.detail, variant: 'destructive' }); }
  };

  if (!vehicle) return null;
  const mapsUrl = (lat && lng) ? `https://www.google.com/maps?q=${lat},${lng}` : null;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><MapPin className="h-5 w-5" /> Location & Photos · <span className="font-mono">{vehicle.equipment_code}</span></DialogTitle>
          <DialogDescription>{vehicle.description}</DialogDescription>
        </DialogHeader>

        <div className="flex gap-1 border-b">
          {TABS.map((t) => (
            <button key={t.k} onClick={() => setTab(t.k)} className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${tab === t.k ? 'border-[hsl(217,91%,24%)] text-[hsl(217,91%,24%)]' : 'border-transparent text-slate-500 hover:text-slate-900'}`} data-testid={`lp-tab-${t.k}`}>
              <t.icon className="h-4 w-4 inline mr-1" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'location' && (
          <div className="space-y-3">
            {locInfo.pending_location && (
              <Alert className="bg-amber-50 border-amber-200 text-amber-900">
                <AlertDescription className="text-xs flex flex-wrap items-center gap-2">
                  <Clock className="h-3.5 w-3.5" /> Pending update: <strong>{locInfo.pending_location.lat}, {locInfo.pending_location.lng}</strong> by {locInfo.pending_location.submitted_by_name} ({formatDateTime(locInfo.pending_location.submitted_at)})
                  {canApprove && (
                    <span className="ml-auto inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => decideLocation('approve')} disabled={busy} data-testid="lp-loc-approve"><CheckCircle2 className="h-3.5 w-3.5 mr-1 text-emerald-600" /> Approve</Button>
                      <Button size="sm" variant="outline" onClick={() => decideLocation('reject')} disabled={busy} data-testid="lp-loc-reject"><XCircle className="h-3.5 w-3.5 mr-1 text-red-600" /> Reject</Button>
                    </span>
                  )}
                </AlertDescription>
              </Alert>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div><Label>Latitude</Label><Input type="number" step="any" value={lat} onChange={(e) => setLat(e.target.value)} placeholder="14.599512" data-testid="lp-lat" /></div>
              <div><Label>Longitude</Label><Input type="number" step="any" value={lng} onChange={(e) => setLng(e.target.value)} placeholder="120.984222" data-testid="lp-lng" /></div>
              <div className="col-span-2"><Label>Location Label (optional)</Label><Input value={label} onChange={(e) => setLabel(e.target.value)} placeholder="e.g. Site B – Block 14 staging area" data-testid="lp-label" /></div>
            </div>

            <div className="flex flex-wrap gap-2">
              {canEditLocation && <Button onClick={useGps} variant="outline" size="sm" data-testid="lp-use-gps"><Locate className="h-4 w-4 mr-1.5" /> Use my GPS</Button>}
              {mapsUrl && <a href={mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center text-sm text-[hsl(217,91%,24%)] hover:underline"><ExternalLink className="h-4 w-4 mr-1" /> Open in Google Maps</a>}
              {canEditLocation && <Button onClick={saveLocation} disabled={busy || !lat || !lng} className="ml-auto bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="lp-loc-save"><Save className="h-4 w-4 mr-1.5" /> {busy ? 'Saving…' : 'Save Location'}</Button>}
            </div>

            {locInfo.location && (
              <div className="p-3 rounded-md border border-slate-200 bg-slate-50 text-xs">
                <div className="font-semibold text-slate-700">Current location</div>
                <div className="font-mono text-sm">{locInfo.location.lat}, {locInfo.location.lng}</div>
                {locInfo.location.label && <div className="text-slate-600 mt-0.5">{locInfo.location.label}</div>}
                <div className="text-[10px] text-slate-500 mt-1">Set by {locInfo.location.submitted_by_name} · {formatDateTime(locInfo.location.submitted_at)} · source: {locInfo.location.source}</div>
              </div>
            )}

            {locInfo.history && locInfo.history.length > 0 && (
              <details className="mt-2">
                <summary className="text-xs uppercase tracking-wider text-slate-500 cursor-pointer flex items-center gap-1"><History className="h-3 w-3" /> Location history ({locInfo.history.length})</summary>
                <ul className="mt-2 space-y-1 text-xs max-h-40 overflow-y-auto">
                  {locInfo.history.map((h) => (
                    <li key={`hist-${h.applied_at || h.submitted_at || ''}-${h.lat}-${h.lng}`} className="border-l-2 border-slate-200 pl-2">
                      <span className="font-mono">{h.lat}, {h.lng}</span>
                      {h.label && <span className="text-slate-600"> – {h.label}</span>}
                      <span className="text-[10px] text-slate-500 ml-2">{h.submitted_by_name} · {formatDateTime(h.applied_at || h.submitted_at)}</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}

        {tab === 'photos' && (
          <div className="space-y-3">
            {canUploadPhoto && (
              <div className="border-2 border-dashed border-slate-300 rounded-md p-4 text-center bg-slate-50"
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => { e.preventDefault(); onFiles(e.dataTransfer.files); }}>
                <ImagePlus className="h-6 w-6 mx-auto text-slate-400" />
                <div className="text-sm mt-1">Drag &amp; drop images here, or</div>
                <Button size="sm" variant="outline" className="mt-2" onClick={() => fileRef.current?.click()} data-testid="lp-photo-pick"><ImagePlus className="h-4 w-4 mr-1" /> Choose files</Button>
                <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onFiles(e.target.files)} />
                <div className="text-[10px] text-slate-500 mt-1">Auto‑compressed to ≤200 KB per image · up to 12 photos per equipment</div>
              </div>
            )}

            {pendingPhotos.length > 0 && (
              <div>
                <div className="text-xs uppercase tracking-wider text-slate-500 mb-2">Ready to upload ({pendingPhotos.length})</div>
                <div className="grid grid-cols-3 gap-2">
                  {pendingPhotos.map((p) => (
                    <div key={p._key} className="border border-slate-200 rounded overflow-hidden bg-white">
                      <img src={p.dataUrl} alt="" className="w-full h-24 object-cover" />
                      <div className="p-2">
                        <Textarea rows={1} className="text-xs" placeholder="Caption (optional)" value={p.caption} onChange={(e) => { const val = e.target.value; setPendingPhotos((prev) => prev.map((x) => x._key === p._key ? { ...x, caption: val } : x)); }} />
                        <Button size="sm" variant="ghost" className="text-xs mt-1" onClick={() => setPendingPhotos((prev) => prev.filter((x) => x._key !== p._key))}>Remove</Button>
                      </div>
                    </div>
                  ))}
                </div>
                <Button size="sm" disabled={photoBusy} onClick={uploadAll} className="mt-2 bg-[hsl(217,91%,24%)] hover:bg-[hsl(217,91%,18%)]" data-testid="lp-photo-upload-all"><Save className="h-4 w-4 mr-1" /> {photoBusy ? 'Uploading…' : `Upload ${pendingPhotos.length} photo(s)`}</Button>
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mt-3 mb-2">Saved photos ({photos.length})</div>
              {photos.length === 0 ? (
                <div className="text-xs text-slate-500 text-center py-6">No photos yet.</div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  {photos.map((p) => (
                    <div key={p.id} className={`border rounded overflow-hidden bg-white ${p.status === 'pending' ? 'border-amber-300' : p.status === 'rejected' ? 'border-red-300 opacity-60' : 'border-slate-200'}`} data-testid={`lp-photo-${p.id}`}>
                      <img src={p.image_base64} alt="" className="w-full h-24 object-cover" />
                      <div className="p-2 space-y-1">
                        <Badge className={`text-[10px] ${p.status === 'pending' ? 'bg-amber-100 text-amber-800' : p.status === 'rejected' ? 'bg-red-100 text-red-800' : 'bg-emerald-100 text-emerald-800'}`}>{p.status}</Badge>
                        {p.caption && <div className="text-[10px] text-slate-700 truncate" title={p.caption}>{p.caption}</div>}
                        <div className="text-[10px] text-slate-500 truncate">{p.uploaded_by_name} · {p.size_kb} KB</div>
                        <div className="flex flex-wrap gap-1 pt-1">
                          {p.status === 'pending' && canApprove && (
                            <>
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => decidePhoto(p.id, 'approve')} data-testid={`lp-photo-approve-${p.id}`}><CheckCircle2 className="h-3 w-3 text-emerald-600 mr-0.5" /> Approve</Button>
                              <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => decidePhoto(p.id, 'reject')} data-testid={`lp-photo-reject-${p.id}`}><XCircle className="h-3 w-3 text-red-600 mr-0.5" /> Reject</Button>
                            </>
                          )}
                          {(canDeletePhotoAny || p.uploaded_by === user?.id) && (
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => deletePhoto(p.id)} data-testid={`lp-photo-delete-${p.id}`}><Trash2 className="h-3 w-3 text-red-600" /></Button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EquipmentLocationPhotos;
